import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, PesticideApplicatorCertificate, PesticideProductRegistration } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const HAZARD_CLASSES = ['CLASS_IA', 'CLASS_IB', 'CLASS_II', 'CLASS_III', 'UNLIKELY_HAZARD'] as const;

type Tab = 'products' | 'applicators';

/** M33 Agriculture — pesticide product registration (WHO hazard class) and applicator certification. */
export function Pesticides() {
  const [tab, setTab] = useState<Tab>('products');
  return (
    <div>
      <PageHeader title="Pesticide Registration" subtitle="Product registration and applicator certification (M33)." />
      <TabStrip tabs={[{ key: 'products', label: 'Product Registrations' }, { key: 'applicators', label: 'Applicator Certificates' }]} active={tab} onChange={setTab} />
      {tab === 'products' ? <ProductsTab /> : <ApplicatorsTab />}
    </div>
  );
}

// ── Products ─────────────────────────────────────────────────────────────

function ProductsTab() {
  const { persona } = useAuth();
  const isAgriOfficer = persona ? persona.roles.includes('AGRI_OFFICER') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m33/pesticide-products?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<PesticideProductRegistration>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Reg no, product name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Active only (default)</option>
            {['APPLIED', 'FEE_PENDING', 'ACTIVE', 'REJECTED', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'WITHDRAWN'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + Register product
        </button>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No pesticide products match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Reg No</th>
                <th className="table-head">Product</th>
                <th className="table-head">Active ingredient</th>
                <th className="table-head">Hazard class</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{p.regNo}</td>
                  <td className="table-cell">{p.productName}</td>
                  <td className="table-cell text-xs text-slate-500">{p.activeIngredient}</td>
                  <td className="table-cell text-xs">{p.hazardClass.replaceAll('_', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <ProductDetailModal id={selectedId} isAgriOfficer={isAgriOfficer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <NewProductModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ProductDetailModal({ id, isAgriOfficer, onClose, onChanged }: { id: string; isAgriOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const pq = useApiQuery<PesticideProductRegistration>(`/api/v1/m33/pesticide-products/${id}`);
  const p = pq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAction, setShowAction] = useState<'approve' | 'reject' | 'suspend' | 'revoke' | null>(null);
  const [note, setNote] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      pq.reload();
      onChanged();
      setShowAction(null);
      setNote('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const reinstate = () => call(() => api.post(`/api/v1/m33/pesticide-products/${id}/reinstate`));
  const withdraw = () => call(() => api.post(`/api/v1/m33/pesticide-products/${id}/withdraw`));
  const confirm = () =>
    call(() => {
      if (showAction === 'approve') return api.post(`/api/v1/m33/pesticide-products/${id}/review`, { approve: true, note: note || undefined });
      if (showAction === 'reject') return api.post(`/api/v1/m33/pesticide-products/${id}/review`, { approve: false, rejectionReason: note });
      if (showAction === 'suspend') return api.post(`/api/v1/m33/pesticide-products/${id}/suspend`, { reason: note });
      return api.post(`/api/v1/m33/pesticide-products/${id}/revoke`, { reason: note });
    });

  return (
    <Modal title={p ? `${p.regNo} — ${p.productName}` : 'Pesticide product'} onClose={onClose}>
      {pq.error ? <ErrorNote message={pq.error} /> : null}
      {pq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={p.status} />} />
            <Detail label="Hazard class" value={p.hazardClass.replaceAll('_', ' ')} />
            <Detail label="Registrant" value={`${p.registrantType}:${p.registrantId}`} mono />
            <Detail label="Use category" value={p.useCategory ?? '—'} />
            <Detail label="Valid to" value={formatDate(p.validTo)} />
            <Detail label="Issued" value={formatDateTime(p.issuedAt)} />
          </div>
          {p.rejectionReason ? <ErrorNote message={`Rejected: ${p.rejectionReason}`} /> : null}
          {p.suspendedReason ? <ErrorNote message={`Suspended: ${p.suspendedReason}`} /> : null}
          {p.revokedReason ? <ErrorNote message={`Revoked: ${p.revokedReason}`} /> : null}

          {isAgriOfficer ? (
            <div className="border-t border-slate-100 pt-4">
              {!showAction ? (
                <div className="flex flex-wrap gap-2">
                  {p.status === 'APPLIED' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowAction('approve')}>
                        Approve
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowAction('reject')}>
                        Reject
                      </button>
                      <button className="btn-secondary" disabled={busy} onClick={withdraw}>
                        Withdraw
                      </button>
                    </>
                  ) : null}
                  {p.status === 'ACTIVE' ? (
                    <button className="btn-secondary" disabled={busy} onClick={() => setShowAction('suspend')}>
                      Suspend
                    </button>
                  ) : null}
                  {p.status === 'SUSPENDED' ? (
                    <button className="btn-success" disabled={busy} onClick={reinstate}>
                      Reinstate
                    </button>
                  ) : null}
                  {p.status === 'ACTIVE' || p.status === 'SUSPENDED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowAction('revoke')}>
                      Revoke
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label={showAction === 'approve' ? 'Note (optional)' : 'Reason'}>
                    <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowAction(null)}>
                      Cancel
                    </button>
                    <button className={showAction === 'approve' ? 'btn-success' : 'btn-danger'} disabled={busy} onClick={confirm}>
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewProductModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [registrantType, setRegistrantType] = useState<'PERSON' | 'ENTITY'>('ENTITY');
  const [registrantId, setRegistrantId] = useState('');
  const [productName, setProductName] = useState('');
  const [activeIngredient, setActiveIngredient] = useState('');
  const [hazardClass, setHazardClass] = useState<(typeof HAZARD_CLASSES)[number]>('CLASS_III');
  const [useCategory, setUseCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/pesticide-products', { registrantType, registrantId, productName, activeIngredient, hazardClass, useCategory: useCategory || undefined });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register pesticide product" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Registrant type">
            <select className="field-input" value={registrantType} onChange={(e) => setRegistrantType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="ENTITY">Entity</option>
              <option value="PERSON">Person</option>
            </select>
          </Field>
          <Field label={registrantType === 'ENTITY' ? 'Business ID' : 'NIN'}>
            <input className="field-input" value={registrantId} onChange={(e) => setRegistrantId(e.target.value)} />
          </Field>
        </div>
        <Field label="Product name">
          <input className="field-input" value={productName} onChange={(e) => setProductName(e.target.value)} />
        </Field>
        <Field label="Active ingredient">
          <input className="field-input" value={activeIngredient} onChange={(e) => setActiveIngredient(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="WHO hazard class">
            <select className="field-input" value={hazardClass} onChange={(e) => setHazardClass(e.target.value as (typeof HAZARD_CLASSES)[number])}>
              {HAZARD_CLASSES.map((h) => (
                <option key={h} value={h}>
                  {h.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Use category (optional)">
            <input className="field-input" value={useCategory} onChange={(e) => setUseCategory(e.target.value)} placeholder="Herbicide" />
          </Field>
        </div>
        <button className="btn-primary w-full" disabled={busy || !registrantId || !productName || !activeIngredient} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit registration'}
        </button>
      </div>
    </Modal>
  );
}

// ── Applicator certificates ──────────────────────────────────────────────

function ApplicatorsTab() {
  const { persona } = useAuth();
  const isAgriOfficer = persona ? persona.roles.includes('AGRI_OFFICER') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m33/pesticide-applicators?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<PesticideApplicatorCertificate>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Cert ref, name, NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['APPLIED', 'TRAINING_SCHEDULED', 'EXAM_PENDING', 'FEE_PENDING', 'ISSUED', 'REJECTED', 'REVOKED', 'EXPIRED', 'WITHDRAWN'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
        {isAgriOfficer ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New applicator application
          </button>
        ) : null}
      </div>
      {!isAgriOfficer ? <RoleNotice roles={['AGRI_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No applicator certificates match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Cert Ref</th>
                <th className="table-head">Name</th>
                <th className="table-head">Training</th>
                <th className="table-head">Exam</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{c.certRef}</td>
                  <td className="table-cell">{c.fullName}</td>
                  <td className="table-cell text-xs text-slate-500">{c.trainingCourseName ?? '—'}</td>
                  <td className="table-cell text-xs">{c.examPassed === null || c.examPassed === undefined ? '—' : c.examPassed ? 'Passed' : 'Failed'}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <ApplicatorDetailModal id={selectedId} isAgriOfficer={isAgriOfficer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <NewApplicatorModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ApplicatorDetailModal({ id, isAgriOfficer, onClose, onChanged }: { id: string; isAgriOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const cq = useApiQuery<PesticideApplicatorCertificate>(`/api/v1/m33/pesticide-applicators/${id}`);
  const c = cq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAction, setShowAction] = useState<'schedule' | 'completed' | 'exam' | 'revoke' | null>(null);
  const [courseName, setCourseName] = useState('');
  const [passed, setPassed] = useState(true);
  const [reason, setReason] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      cq.reload();
      onChanged();
      setShowAction(null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const confirm = () =>
    call(() => {
      const now = new Date().toISOString();
      if (showAction === 'schedule') return api.post(`/api/v1/m33/pesticide-applicators/${id}/schedule-training`, { courseName });
      if (showAction === 'completed') return api.post(`/api/v1/m33/pesticide-applicators/${id}/training-completed`, { completedAt: now });
      if (showAction === 'exam') return api.post(`/api/v1/m33/pesticide-applicators/${id}/exam-result`, { passed, examDate: now });
      return api.post(`/api/v1/m33/pesticide-applicators/${id}/revoke`, { reason });
    });

  return (
    <Modal title={c ? `${c.certRef} — ${c.fullName}` : 'Applicator certificate'} onClose={onClose}>
      {cq.error ? <ErrorNote message={cq.error} /> : null}
      {cq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={c.status} />} />
            <Detail label="NIN" value={c.nin} mono />
            <Detail label="Training course" value={c.trainingCourseName ?? '—'} />
            <Detail label="Training completed" value={formatDate(c.trainingCompletedAt)} />
            <Detail label="Exam" value={c.examPassed === null || c.examPassed === undefined ? '—' : c.examPassed ? 'Passed' : 'Failed'} />
            <Detail label="Valid to" value={formatDate(c.validTo)} />
          </div>
          {c.rejectionReason ? <ErrorNote message={`Rejected: ${c.rejectionReason}`} /> : null}
          {c.revokedReason ? <ErrorNote message={`Revoked: ${c.revokedReason}`} /> : null}

          {isAgriOfficer ? (
            <div className="border-t border-slate-100 pt-4">
              {!showAction ? (
                <div className="flex flex-wrap gap-2">
                  {c.status === 'APPLIED' ? (
                    <button className="btn-primary" disabled={busy} onClick={() => setShowAction('schedule')}>
                      Schedule training
                    </button>
                  ) : null}
                  {c.status === 'TRAINING_SCHEDULED' ? (
                    <button className="btn-primary" disabled={busy} onClick={() => setShowAction('completed')}>
                      Mark training completed
                    </button>
                  ) : null}
                  {c.status === 'EXAM_PENDING' ? (
                    <button className="btn-primary" disabled={busy} onClick={() => setShowAction('exam')}>
                      Record exam result
                    </button>
                  ) : null}
                  {c.status === 'ISSUED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowAction('revoke')}>
                      Revoke
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  {showAction === 'schedule' ? (
                    <Field label="Course name">
                      <input className="field-input" value={courseName} onChange={(e) => setCourseName(e.target.value)} />
                    </Field>
                  ) : null}
                  {showAction === 'exam' ? (
                    <Field label="Result">
                      <select className="field-input" value={passed ? 'pass' : 'fail'} onChange={(e) => setPassed(e.target.value === 'pass')}>
                        <option value="pass">Passed</option>
                        <option value="fail">Failed</option>
                      </select>
                    </Field>
                  ) : null}
                  {showAction === 'revoke' ? (
                    <Field label="Reason">
                      <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                  ) : null}
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowAction(null)}>
                      Cancel
                    </button>
                    <button className="btn-primary" disabled={busy || (showAction === 'schedule' && !courseName) || (showAction === 'revoke' && reason.trim().length < 3)} onClick={confirm}>
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewApplicatorModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [nin, setNin] = useState('');
  const [fullName, setFullName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/pesticide-applicators', { nin, fullName });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New applicator certification application" onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <Field label="Full name">
          <input className="field-input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !nin || !fullName} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </Modal>
  );
}
